"use client";

import { FetchCallout } from "@/fetch-ui/FetchCallout";
import atlanta from "@/app/(public)/shared/assets/venues/atlanta.jpg";
import barcelona from "@/app/(public)/shared/assets/venues/barcelona.jpg";
import barcelona2 from "@/app/(public)/shared/assets/venues/barcelona_2.jpg";
import dallas from "@/app/(public)/shared/assets/venues/dallas.webp";
import dominicanRepublic from "@/app/(public)/shared/assets/venues/dominicanrepublic.jpg";
import fourSeasonsHawaii from "@/app/(public)/shared/assets/venues/fourseasons-hawaii.jpg";
import maui from "@/app/(public)/shared/assets/venues/maui.webp";
import mexicoCity from "@/app/(public)/shared/assets/venues/mexicocity.webp";
import rimba from "@/app/(public)/shared/assets/venues/rimba.webp";
import stRegisMaldives from "@/app/(public)/shared/assets/venues/stregismaldivesvommuliresort.jpg";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { EmailSignIn } from "./EmailSignIn";
import { GoogleSignIn } from "./GoogleSignIn";
import { SSOSignIn } from "./SSOSignIn";

interface SignInProps {
  redirectTo?: string;
  /** When set, renders a "you were invited to <event>" (or, with `isRequireLogin`, a
   *  "sign in to access <event>") banner below the welcome subheading. */
  invitedEventName?: string;
  /** When true, swaps the invited-event banner copy for a page/form that grants on
   *  any signed-in account, no invite list — "sign in with the email your invitation
   *  was sent to" would be misleading since there's no specific invited email. */
  isRequireLogin?: boolean;
  /** When set, renders a warning callout (e.g. admin re-auth required) above the sign-in buttons. */
  notice?: string;
}

const venueImages = [
  atlanta,
  barcelona,
  barcelona2,
  dallas,
  dominicanRepublic,
  fourSeasonsHawaii,
  maui,
  mexicoCity,
  rimba,
  stRegisMaldives,
];

export const SignIn = ({ redirectTo, invitedEventName, isRequireLogin, notice }: SignInProps) => {
  // States
  const [showOtp, setShowOtp] = useState<boolean>(false);

  // Render
  return (
    <div className="flex flex-row h-screen w-screen bg-landing-primary">
      {/* Logo */}
      <div className="absolute top-4 left-4 md:top-6 md:left-8">
        <Image src="/nowadays_black.svg" alt="Nowadays Logo" width={160} height={60} className="w-32 md:w-40" />
      </div>

      <div className="flex flex-col items-center justify-center w-full md:w-1/2">
        <div className="flex flex-col items-center justify-center p-6 md:p-8 w-full max-w-[500px] gap-6 md:gap-8 px-8">
          {!showOtp && (
            <>
              <div className="flex flex-col items-center justify-center gap-2">
                <p className="text-2xl md:text-3xl font-semibold text-gray-800">Welcome to Nowadays</p>
                <p className="text-sm font-medium text-gray-500 text-center">
                  Sign in or create a free account to start planning events
                </p>
              </div>

              {notice && (
                <FetchCallout variant="warning" className="w-full">
                  <span>{notice}</span>
                </FetchCallout>
              )}

              {invitedEventName && (
                <FetchCallout variant="info" className="w-full">
                  <span>
                    {isRequireLogin ? (
                      <>
                        Sign in to access <span className="font-semibold">{invitedEventName}</span>.
                      </>
                    ) : (
                      <>
                        You were invited to <span className="font-semibold">{invitedEventName}</span>. Sign in with the
                        email your invitation was sent to.
                      </>
                    )}
                  </span>
                </FetchCallout>
              )}

              <div className="flex flex-col gap-2 w-full">
                <GoogleSignIn redirectTo={redirectTo} />
                <SSOSignIn redirectTo={redirectTo} />
              </div>

              <div className="flex flex-row gap-3 items-center justify-center w-full">
                <div className="flex-1 h-px bg-gray-100"></div>
                <p className="text-sm font-medium text-gray-500 whitespace-nowrap">or continue with email</p>
                <div className="flex-1 h-px bg-gray-100"></div>
              </div>
            </>
          )}

          <EmailSignIn redirectTo={redirectTo} onOtpStateChange={setShowOtp} />

          {!showOtp && (
            <p className="text-center text-sm font-medium text-gray-500">
              By continuing, you agree to our{" "}
              <Link href="https://nowadays.ai/terms-of-service" className="text-primary hover:underline">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link href="https://nowadays.ai/privacy-policy" className="text-primary hover:underline">
                Privacy Policy
              </Link>
              .
            </p>
          )}
        </div>
      </div>

      <div className="hidden md:block w-1/2">
        <Image
          src={venueImages[Math.floor(Math.random() * venueImages.length)].src}
          alt="Event planning"
          width={4000}
          height={4000}
          className="w-full h-full object-cover transition-opacity duration-1000"
          priority
          aria-label="Decorative image of event planning"
          tabIndex={-1}
          role="presentation"
        />
      </div>
    </div>
  );
};
